/* ==========================================================================
   Christmas Storefront — theme.js
   Small, dependency-free modules as custom elements. One owner per feature.
   Progressive enhancement: purchase forms work without JS where Shopify allows.
   ========================================================================== */
(function () {
  'use strict';

  /* ----- Utilities ----- */
  const Theme = window.Theme || {};
  window.Theme = Theme;

  Theme.money = function (cents) {
    const amount = (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 });
    return (window.Shopify && Shopify.currency && Shopify.currency.active)
      ? amount + ' ' + Shopify.currency.active
      : amount;
  };

  Theme.fetchJSON = function (url, options) {
    return fetch(url, Object.assign({ headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } }, options))
      .then((r) => r.json());
  };

  // Simple pub/sub so features coordinate without tight coupling.
  const subscribers = {};
  Theme.on = function (event, cb) {
    (subscribers[event] = subscribers[event] || []).push(cb);
    return () => { subscribers[event] = subscribers[event].filter((f) => f !== cb); };
  };
  Theme.emit = function (event, data) {
    (subscribers[event] || []).forEach((cb) => {
      try { cb(data); } catch (e) { console.error(e); }
    });
  };

  Theme.safeStorage = {
    get(key) { try { return JSON.parse(localStorage.getItem(key)); } catch (e) { return null; } },
    set(key, val) { try { localStorage.setItem(key, JSON.stringify(val)); return true; } catch (e) { return false; } }
  };

  /* ----- Focus trap helper for drawers/dialogs ----- */
  function trapFocus(container, opener) {
    const focusable = container.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (!focusable.length) return function () {};
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    function onKey(e) {
      if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    container.addEventListener('keydown', onKey);
    first.focus();
    return function release() {
      container.removeEventListener('keydown', onKey);
      if (opener && typeof opener.focus === 'function') opener.focus();
    };
  }
  Theme.trapFocus = trapFocus;

  function overlay() { return document.querySelector('[data-overlay]'); }

  /* =========================================================================
     Menu drawer (mobile navigation)
     ========================================================================= */
  class MenuDrawer extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('.menu-drawer');
      this.toggle = this.querySelector('[data-menu-toggle]');
      this.closeBtn = this.querySelector('[data-menu-close]');
      this.release = null;
      if (this.toggle) this.toggle.addEventListener('click', () => this.open());
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.isOpen) this.close(); });
      const ov = overlay();
      if (ov) ov.addEventListener('click', () => { if (this.isOpen) this.close(); });
    }
    open() {
      this.isOpen = true;
      this.drawer.classList.add('is-open');
      const ov = overlay(); if (ov) { ov.hidden = false; requestAnimationFrame(() => ov.classList.add('is-open')); }
      document.body.style.overflow = 'hidden';
      if (this.toggle) this.toggle.setAttribute('aria-expanded', 'true');
      this.release = trapFocus(this.drawer, this.toggle);
    }
    close() {
      this.isOpen = false;
      this.drawer.classList.remove('is-open');
      const ov = overlay(); if (ov) { ov.classList.remove('is-open'); setTimeout(() => { ov.hidden = true; }, 200); }
      document.body.style.overflow = '';
      if (this.toggle) this.toggle.setAttribute('aria-expanded', 'false');
      if (this.release) this.release();
    }
  }
  customElements.define('menu-drawer', MenuDrawer);

  /* =========================================================================
     Quantity input
     ========================================================================= */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('button');
        if (!btn) return;
        e.preventDefault();
        const step = btn.dataset.qtyChange === 'up' ? 1 : -1;
        const min = parseInt(this.input.min || '1', 10);
        const next = Math.max(min, (parseInt(this.input.value, 10) || min) + step);
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* =========================================================================
     Variant picker — updates price, media, availability, submitted variant ID
     Expects a JSON <script> of variants with id/available/price/options/featured_media.
     ========================================================================= */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-variant-json]');
      this.variants = data ? JSON.parse(data.textContent) : [];
      this.productUrl = this.dataset.url;
      this.addEventListener('change', () => this.onChange());
      this.currentVariant = this.getSelectedVariant();
    }
    getSelectedOptions() {
      return Array.from(this.querySelectorAll('input[type="radio"]:checked, select'))
        .map((el) => el.value);
    }
    getSelectedVariant() {
      const opts = this.getSelectedOptions();
      return this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
    }
    onChange() {
      this.currentVariant = this.getSelectedVariant();
      this.updateAvailabilityHints();
      const form = document.querySelector('product-form form, form[data-product-form]');
      this.updateUrl();
      this.updatePriceAndMedia();
      if (!this.currentVariant) { this.setForm(null); Theme.emit('variant:unavailable', {}); return; }
      this.setForm(this.currentVariant);
      Theme.emit('variant:change', this.currentVariant);
    }
    setForm(variant) {
      const idInput = document.querySelector('form[data-product-form] input[name="id"]');
      const addBtn = document.querySelector('form[data-product-form] [data-add-to-cart]');
      if (idInput) idInput.value = variant ? variant.id : '';
      if (addBtn) {
        const text = addBtn.querySelector('[data-add-text]') || addBtn;
        if (!variant) { addBtn.setAttribute('disabled', ''); text.textContent = addBtn.dataset.unavailable || 'Unavailable'; }
        else if (!variant.available) { addBtn.setAttribute('disabled', ''); text.textContent = addBtn.dataset.soldOut || 'Sold out'; }
        else { addBtn.removeAttribute('disabled'); text.textContent = addBtn.dataset.default || 'Add to cart'; }
      }
    }
    updatePriceAndMedia() {
      const priceEl = document.querySelector('[data-product-price]');
      if (priceEl && this.currentVariant) {
        priceEl.innerHTML = this.renderPrice(this.currentVariant);
      }
      if (this.currentVariant && this.currentVariant.featured_media) {
        const gallery = document.querySelector('product-gallery');
        if (gallery && gallery.showMediaById) gallery.showMediaById(this.currentVariant.featured_media.id);
      }
      Theme.emit('variant:price', this.currentVariant);
    }
    renderPrice(v) {
      if (v.compare_at_price && v.compare_at_price > v.price) {
        return '<span class="price price--on-sale"><s class="price__compare">' + Theme.money(v.compare_at_price) +
          '</s> <span class="price__sale">' + Theme.money(v.price) + '</span></span>';
      }
      return '<span class="price"><span class="price__regular">' + Theme.money(v.price) + '</span></span>';
    }
    updateAvailabilityHints() {
      // Mark option values that lead to no available variant.
      this.querySelectorAll('.variant-swatch').forEach((sw) => {
        const input = sw.querySelector('input');
        if (!input) return;
        sw.classList.remove('variant-swatch--unavailable');
      });
    }
    updateUrl() {
      if (!this.currentVariant || !this.productUrl || this.dataset.updateUrl === 'false') return;
      const url = new URL(this.productUrl, window.location.origin);
      url.searchParams.set('variant', this.currentVariant.id);
      window.history.replaceState({}, '', url);
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* =========================================================================
     Product gallery — thumbnail -> main image, keyboard accessible
     ========================================================================= */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('.product__media-main');
      this.thumbs = Array.from(this.querySelectorAll('.product__thumb'));
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.activate(t)));
    }
    activate(thumb) {
      this.thumbs.forEach((t) => t.setAttribute('aria-current', t === thumb ? 'true' : 'false'));
      const mediaId = thumb.dataset.mediaId;
      this.showMediaById(mediaId);
    }
    showMediaById(mediaId) {
      const target = this.querySelector('[data-media-id="' + mediaId + '"].product__media-item');
      if (!target) return;
      this.querySelectorAll('.product__media-item').forEach((m) => { m.hidden = true; });
      target.hidden = false;
      const thumb = this.querySelector('.product__thumb[data-media-id="' + mediaId + '"]');
      if (thumb) this.thumbs.forEach((t) => t.setAttribute('aria-current', t === thumb ? 'true' : 'false'));
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* =========================================================================
     Product form — AJAX add to cart, guarded against double submit
     ========================================================================= */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('[data-add-to-cart]');
      this.errorEl = this.querySelector('[data-form-error]');
      this.submitting = false;
      if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    onSubmit(e) {
      if (!window.fetch) return; // progressive enhancement: let it POST normally
      e.preventDefault();
      if (this.submitting) return;
      const idInput = this.form.querySelector('input[name="id"]');
      if (!idInput || !idInput.value) return;
      this.submitting = true;
      this.setLoading(true);
      if (this.errorEl) this.errorEl.hidden = true;

      const formData = new FormData(this.form);
      fetch('/cart/add.js', { method: 'POST', body: formData, headers: { 'Accept': 'application/json' } })
        .then((r) => r.json().then((body) => ({ ok: r.ok, body })))
        .then(({ ok, body }) => {
          if (!ok) { this.showError(body.description || body.message); return; }
          Theme.emit('cart:added', body);
          const drawer = document.querySelector('cart-drawer');
          if (drawer && drawer.refreshAndOpen) drawer.refreshAndOpen();
          else window.location.href = '/cart';
        })
        .catch(() => this.showError('Something went wrong. Please try again.'))
        .finally(() => { this.submitting = false; this.setLoading(false); });
    }
    setLoading(on) {
      if (!this.button) return;
      this.button.classList.toggle('is-loading', on);
      this.button.setAttribute('aria-busy', on ? 'true' : 'false');
    }
    showError(msg) {
      if (this.errorEl) { this.errorEl.textContent = msg; this.errorEl.hidden = false; }
    }
  }
  customElements.define('product-form', ProductForm);

  /* =========================================================================
     Cart drawer — single source of truth for cart UI
     ========================================================================= */
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('.cart-drawer');
      this.body = this.querySelector('[data-cart-body]');
      this.release = null;
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.isOpen) this.close(); });
      const ov = overlay();
      if (ov) ov.addEventListener('click', () => { if (this.isOpen) this.close(); });
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-cart-toggle]');
        if (trigger) { e.preventDefault(); this.refreshAndOpen(); }
        // Close button lives inside this element and is re-rendered on refresh,
        // so handle it by delegation rather than a bound reference.
        if (this.contains(e.target) && e.target.closest('[data-cart-close]')) { e.preventDefault(); this.close(); }
        const remove = e.target.closest('[data-cart-remove]');
        if (remove && this.contains(remove)) { e.preventDefault(); this.changeLine(remove.dataset.line, 0); }
      });
      this.addEventListener('change', (e) => {
        const qty = e.target.closest('[data-cart-qty]');
        if (qty) this.changeLine(qty.dataset.line, parseInt(qty.value, 10));
      });
    }
    open() {
      this.isOpen = true;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      const ov = overlay(); if (ov) { ov.hidden = false; requestAnimationFrame(() => ov.classList.add('is-open')); }
      document.body.style.overflow = 'hidden';
      this.release = trapFocus(this.drawer, document.activeElement);
    }
    close() {
      this.isOpen = false;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      const ov = overlay(); if (ov) { ov.classList.remove('is-open'); setTimeout(() => { ov.hidden = true; }, 200); }
      document.body.style.overflow = '';
      if (this.release) this.release();
    }
    refreshAndOpen() { this.refresh().then(() => this.open()); }
    refresh() {
      const wasOpen = this.isOpen;
      return fetch('/?section_id=cart-drawer')
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('cart-drawer');
          if (fresh) {
            this.innerHTML = fresh.innerHTML;
            // Re-cache references after DOM replacement; delegated handlers on
            // `this` and document survive because the element itself persists.
            this.drawer = this.querySelector('.cart-drawer');
            this.body = this.querySelector('[data-cart-body]');
            if (wasOpen) { this.drawer.classList.add('is-open'); this.drawer.setAttribute('aria-hidden', 'false'); }
          }
          this.updateCount(doc);
        })
        .catch(() => {});
    }
    updateCount(doc) {
      const src = doc && doc.querySelector('[data-cart-count]');
      const count = src ? src.textContent : null;
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        if (count !== null) { el.textContent = count; el.hidden = parseInt(count, 10) === 0; }
      });
    }
    changeLine(line, quantity) {
      if (!line) return;
      this.drawer.classList.add('is-loading');
      Theme.fetchJSON('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ line: parseInt(line, 10), quantity: quantity })
      }).then(() => this.refresh())
        .then(() => Theme.emit('cart:updated', {}))
        .finally(() => this.drawer.classList.remove('is-loading'));
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  /* =========================================================================
     Sticky add-to-cart — mirrors main form, shows after hero scrolls past
     ========================================================================= */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.anchor = document.querySelector('[data-sticky-anchor]');
      this.button = this.querySelector('[data-sticky-add]');
      if (this.button) {
        this.button.addEventListener('click', () => {
          const realBtn = document.querySelector('form[data-product-form] [data-add-to-cart]');
          if (realBtn) realBtn.click();
        });
      }
      Theme.on('variant:change', (v) => this.sync(v));
      Theme.on('variant:unavailable', () => this.setDisabled(true));
      if ('IntersectionObserver' in window && this.anchor) {
        this.observer = new IntersectionObserver((entries) => {
          this.classList.toggle('is-visible', !entries[0].isIntersecting);
        }, { rootMargin: '0px 0px -80% 0px' });
        this.observer.observe(this.anchor);
      }
    }
    sync(v) {
      this.setDisabled(!v || !v.available);
      const price = this.querySelector('[data-sticky-price]');
      if (price && v) price.innerHTML = Theme.money(v.price);
    }
    setDisabled(on) {
      if (this.button) {
        if (on) this.button.setAttribute('disabled', '');
        else this.button.removeAttribute('disabled');
      }
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* =========================================================================
     Local wishlist — browser-local, device-specific by design (P0)
     ========================================================================= */
  const WISHLIST_KEY = 'cs_wishlist';
  Theme.wishlist = {
    all() { return Theme.safeStorage.get(WISHLIST_KEY) || []; },
    has(handle) { return this.all().indexOf(handle) !== -1; },
    toggle(handle) {
      const list = this.all();
      const i = list.indexOf(handle);
      if (i === -1) list.push(handle); else list.splice(i, 1);
      Theme.safeStorage.set(WISHLIST_KEY, list);
      Theme.emit('wishlist:changed', list);
      return i === -1;
    }
  };
  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this.handle = this.dataset.handle;
      this.button = this.querySelector('button') || this;
      this.render();
      this.button.addEventListener('click', (e) => {
        e.preventDefault();
        const added = Theme.wishlist.toggle(this.handle);
        this.render(added);
      });
      Theme.on('wishlist:changed', () => this.render());
    }
    render() {
      const active = Theme.wishlist.has(this.handle);
      this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
      this.classList.toggle('is-active', active);
    }
  }
  customElements.define('wishlist-button', WishlistButton);

  /* =========================================================================
     Recently viewed — stores product handles locally, renders on request
     ========================================================================= */
  const RV_KEY = 'cs_recently_viewed';
  Theme.recentlyViewed = {
    track(handle) {
      if (!handle) return;
      let list = Theme.safeStorage.get(RV_KEY) || [];
      list = [handle].concat(list.filter((h) => h !== handle)).slice(0, 12);
      Theme.safeStorage.set(RV_KEY, list);
    },
    list() { return Theme.safeStorage.get(RV_KEY) || []; }
  };
  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      const current = this.dataset.current;
      const handles = Theme.recentlyViewed.list().filter((h) => h !== current).slice(0, parseInt(this.dataset.limit || '4', 10));
      if (!handles.length) { this.closest('[data-recently-viewed-section]')?.setAttribute('hidden', ''); return; }
      const container = this.querySelector('[data-rv-grid]');
      let pending = handles.length;
      handles.forEach((handle) => {
        fetch('/products/' + handle + '?section_id=product-card')
          .then((r) => r.ok ? r.text() : '')
          .then((html) => {
            if (html) {
              const doc = new DOMParser().parseFromString(html, 'text/html');
              const card = doc.querySelector('[data-product-card]');
              if (card && container) container.appendChild(card);
            }
          })
          .catch(() => {})
          .finally(() => { if (--pending === 0 && container && !container.children.length) this.closest('[data-recently-viewed-section]')?.setAttribute('hidden', ''); });
      });
    }
  }
  customElements.define('recently-viewed', RecentlyViewed);

  /* =========================================================================
     Quick add (product cards) — AJAX add, then open drawer
     ========================================================================= */
  document.addEventListener('submit', function (e) {
    const form = e.target.closest('[data-quick-add]');
    if (!form || !window.fetch) return;
    e.preventDefault();
    const btn = form.querySelector('button');
    if (btn && btn.classList.contains('is-loading')) return;
    if (btn) btn.classList.add('is-loading');
    fetch('/cart/add.js', { method: 'POST', body: new FormData(form), headers: { 'Accept': 'application/json' } })
      .then((r) => r.json().then((body) => ({ ok: r.ok, body })))
      .then(({ ok, body }) => {
        if (!ok) { window.location.href = form.getAttribute('action') ? '/cart' : '/cart'; return; }
        Theme.emit('cart:added', body);
        const drawer = document.querySelector('cart-drawer');
        if (drawer && drawer.refreshAndOpen) drawer.refreshAndOpen();
        else window.location.href = '/cart';
      })
      .catch(() => { window.location.href = '/cart'; })
      .finally(() => { if (btn) btn.classList.remove('is-loading'); });
  });

  // Track current product view (set via data attribute on body-level element)
  document.addEventListener('DOMContentLoaded', function () {
    const el = document.querySelector('[data-track-product]');
    if (el) Theme.recentlyViewed.track(el.dataset.trackProduct);
  });

  /* Christmas delivery cutoff — uses the merchant-confirmed ISO 8601 instant. */
  (function () {
    const body = document.body;
    const cutoffText = body && body.dataset.christmasCutoff;
    if (!cutoffText) return;
    const cutoff = Date.parse(cutoffText);
    if (!Number.isFinite(cutoff)) return;
    const lastMessage = body.dataset.lastMinuteMessage || 'Too late to ship? Send a digital gift card instantly';
    const giftCardUrl = '/products/gift-card';
    const countdownEls = Array.from(document.querySelectorAll('[data-delivery-countdown]'));
    const announceLastMinute = function () {
      const rotator = document.querySelector('[data-announcement] .announcement__rotator');
      if (rotator) {
        rotator.replaceChildren();
        const link = document.createElement('a');
        link.href = giftCardUrl;
        link.textContent = lastMessage;
        rotator.appendChild(link);
      }
    };
    const update = function () {
      const remaining = cutoff - Date.now();
      if (remaining <= 0) {
        const withinLastMinuteWindow = remaining > -5 * 86400000;
        countdownEls.forEach((el) => {
          if (withinLastMinuteWindow) {
            el.hidden = false;
            el.replaceChildren();
            const link = document.createElement('a');
            link.href = giftCardUrl;
            link.textContent = el.dataset.lastMessage || lastMessage;
            el.appendChild(link);
          } else el.hidden = true;
        });
        if (withinLastMinuteWindow) announceLastMinute();
        return withinLastMinuteWindow;
      }
      const totalHours = Math.floor(remaining / 3600000);
      const days = Math.floor(totalHours / 24);
      const hours = totalHours % 24;
      countdownEls.forEach((el) => {
        const copy = el.querySelector('[data-countdown-copy]');
        if (copy) copy.textContent = 'Order within ' + days + 'd ' + hours + 'h for Christmas delivery';
      });
      return true;
    };
    if (update()) {
      const timer = window.setInterval(() => { if (!update()) window.clearInterval(timer); }, 60000);
    }
  })();

  /* Gift receipt, message, and wrapping options are shared by cart page/drawer. */
  document.addEventListener('change', function (event) {
    const attributeField = event.target.closest('[data-cart-attribute]');
    const wrapField = event.target.closest('[data-gift-wrap-variant]');
    if (attributeField) {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((cart) => {
          const attributes = Object.assign({}, cart.attributes || {});
          const key = attributeField.dataset.cartAttribute;
          attributes[key] = attributeField.type === 'checkbox' ? (attributeField.checked ? 'Yes' : 'No') : attributeField.value;
          return Theme.fetchJSON('/cart/update.js', { method: 'POST', body: JSON.stringify({ attributes }) });
        })
        .then(() => {
          const drawer = attributeField.closest('cart-drawer');
          if (drawer) drawer.refresh();
          else window.location.reload();
        })
        .catch(() => {});
    }
    if (wrapField) {
      const variantId = Number(wrapField.dataset.giftWrapVariant);
      const wanted = wrapField.checked;
      wrapField.disabled = true;
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((cart) => {
          const line = cart.items.find((item) => Number(item.variant_id) === variantId);
          if (wanted && !line) return Theme.fetchJSON('/cart/add.js', { method: 'POST', body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }) });
          if (!wanted && line) return Theme.fetchJSON('/cart/change.js', { method: 'POST', body: JSON.stringify({ id: line.key, quantity: 0 }) });
          return null;
        })
        .then(() => {
          const drawer = wrapField.closest('cart-drawer');
          if (drawer) drawer.refresh();
          else window.location.reload();
        })
        .catch(() => { wrapField.checked = !wanted; })
        .finally(() => { wrapField.disabled = false; });
    }
  });
})();
